import { createStyles, Grid, GridSize, makeStyles, Theme, Typography } from '@agentnet/components';
import { useFeatureFlag } from '@agentnet/feature-flags';
import {
  CellStatus,
  Content,
  DashboardMetricCard,
  DataTable,
  PageHeader,
  pxToRem,
} from '@fluentsms/agentnet-web-components';
import {
  ColDef,
  FilterChangedEvent,
  GridApi,
  ICellRendererParams,
  SelectionChangedEvent,
  SortChangedEvent,
} from 'ag-grid-enterprise';
import clsx from 'clsx';
import { format as formatDate } from 'date-fns';
import { KBLinks } from 'features/constants';
import { getMemberNames, RemittanceReceivablesColor, RemittanceReceivablesStatus } from 'features/orderManagement/enum';
import { RemittanceReceivable } from 'features/orderManagement/types';
import { useRef, useState } from 'react';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { RemittanceReceivablesModifyAssignmentContainer } from './RemittanceReceivablesModifyAssignmentContainer';

export function RemittanceReceivablesContent() {
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);
  const isRemittanceReceivablesNavEnabled = useFeatureFlag(FeatureFlag.SHOW_REMITTANCE_RECEIVABLES_NAVIGATION, false);
  const [selectedRows, setSelectedRows] = useState<RemittanceReceivable[]>([]);
  const [modifyAssignmentDrawerOpen, setEditAssignmentDrawerOpen] = useState<boolean>(false);
  const [editAssignmentData, setEditAssignmentData] = useState<RemittanceReceivable[]>([]);
  const gridApiRef = useRef<GridApi<RemittanceReceivable> | null>(null);

  const headerCheckboxRenderer = () => {
    return <input type="checkbox" data-testid={'OrderManagement-RemittanceReceivables-HeaderCheckbox'} />;
  };

  const rowCheckboxRenderer = () => {
    return <input type="checkbox" data-testid={'OrderManagement-RemittanceReceivables-RowCheckbox'} />;
  };

  const processDateRenderer = (params: { value: Date }) => {
    return <div>{params.value ? formatDate(params.value, 'MM/dd/yyyy') : ''}</div>;
  };

  const getColorsByStatus = (): { [status: string]: string } => {
    const colors: { [status: string]: string } = {};
    const statusColors = getMemberNames(RemittanceReceivablesColor);
    let statusColor = statusColors.next();
    while (!statusColor.done) {
      const colorCode = RemittanceReceivablesColor[statusColor.value as keyof typeof RemittanceReceivablesColor];
      colors[statusColor.value] = '#' + colorCode.toString(16).padStart(6, '0');
      statusColor = statusColors.next();
    }
    return colors;
  };

  const colorsByStatus = getColorsByStatus();

  const formatUSD = (totalSelectedAmount: any): string => {
    return totalSelectedAmount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const [colDef, setColDef] = useState<ColDef[]>([
    {
      colId: 'checkboxColumn',
      field: '',
      maxWidth: 40,
      filter: false,
      floatingFilter: false,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      showDisabledCheckboxes: true,
      pinned: 'left',
      headerComponent: headerCheckboxRenderer,
      cellRenderer: rowCheckboxRenderer,
      resizable: false,
      suppressMovable: true,
    },
    {
      colId: 'batchColumn',
      field: 'batch',
      headerName: 'Batch',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      minWidth: 144,
      enableRowGroup: true,
    },
    {
      colId: 'itemColumn',
      field: 'item',
      headerName: 'Item',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      enableRowGroup: true,
      minWidth: 144,
      maxWidth: 256,
    },
    {
      colId: 'categoryColumn',
      field: 'category',
      headerName: 'Category',
      filter: 'agSetColumnFilter',
      suppressFloatingFilterButton: false,
      minWidth: 144,
      maxWidth: 256,
    },
    {
      colId: 'statusColumn',
      field: 'status',
      headerName: 'Status',
      cellRenderer: (params: ICellRendererParams) => {
        return <CellStatus status={params.value} colorsByStatus={colorsByStatus} />;
      },
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      suppressFloatingFilterButton: false,
      minWidth: 144,
      maxWidth: 256,
    },
    {
      colId: 'orderIdColumn',
      field: 'orderId',
      headerName: 'Order ID',
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      minWidth: 144,
      maxWidth: 256,
    },
    {
      colId: 'entityNameColumn',
      field: 'entityName',
      headerName: 'Entity Name',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      enableRowGroup: true,
      minWidth: 144,
      maxWidth: 256,
    },
    {
      colId: 'lockboxNameColumn',
      field: 'lockboxName',
      headerName: 'Lockbox Name',
      filter: 'agSetColumnFilter',
      suppressFloatingFilterButton: false,
      minWidth: 144,
      maxWidth: 256,
    },
    {
      colId: 'paymentMethodColumn',
      field: 'paymentMethod',
      headerName: 'Payment Method',
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      suppressFloatingFilterButton: false,
      minWidth: 144,
      maxWidth: 256,
    },
    {
      colId: 'processDateColumn',
      field: 'processDate',
      type: 'dateColumn',
      cellDataType: 'date',
      headerName: 'Process Date',
      cellRenderer: processDateRenderer,
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      sort: 'desc',
      minWidth: 188,
      maxWidth: 188,
    },
    {
      colId: 'assigneeColumn',
      field: 'assignee',
      headerName: 'Assignee',
      filter: 'agTextColumnFilter',
      suppressFloatingFilterButton: false,
      enableRowGroup: true,
      minWidth: 166,
      maxWidth: 300,
    },
    {
      colId: 'amountColumn',
      field: 'amount',
      headerName: 'Amount',
      filter: 'agNumberColumnFilter',
      enableRowGroup: true,
      suppressFloatingFilterButton: false,
      cellRenderer: (params: ICellRendererParams) => {
        return <div>{formatUSD(params.value)}</div>;
      },
      minWidth: 144,
      maxWidth: 256,
    },
  ]);

  const [filteredRows, setFilteredRows] = useState<any[] | null>(null);
  const [rowData, setRowData] = useState<RemittanceReceivable[]>([
    // tentative data for demonstration purposes
    {
      batch: 'Batch 1',
      item: 'Item 1',
      category: '',
      status: 'Assigned',
      orderId: 12345,
      entityName: 'Entity 1',
      lockboxName: 'Lockbox 1',
      paymentMethod: 'Credit Card',
      processDate: new Date('2025-03-04'),
      assignee: 'John Doe',
      amount: 100.0,
    },
    {
      batch: 'Batch 2',
      item: 'Item 2',
      category: '',
      status: 'Unassigned',
      orderId: 67890,
      entityName: 'Entity 2',
      lockboxName: 'Lockbox 2',
      paymentMethod: 'Bank Transfer',
      processDate: new Date('2023-10-02'),
      assignee: 'Jane Smith',
      amount: 200.0,
    },
    {
      batch: 'Batch 3',
      item: 'Item 3',
      category: '',
      status: 'Closed',
      orderId: 11223,
      entityName: 'Entity 3',
      lockboxName: 'Lockbox 3',
      paymentMethod: 'Check',
      processDate: new Date('2022-01-01'),
      assignee: 'Alice Johnson',
      amount: 300.0,
    },
    {
      batch: 'Batch 3',
      item: 'Item 4',
      category: '',
      status: 'Closed',
      orderId: 98765,
      entityName: 'Entity 4',
      lockboxName: 'Lockbox 2',
      paymentMethod: 'Check',
      processDate: new Date('2019-01-01'),
      assignee: 'Bugs Bunny',
      amount: 400.0,
    },
  ]);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      root: {
        width: '100%',
        marginTop: pxToRem(114),
      },
      selectedRows: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100% !important',
        fontSize: pxToRem(14),
        padding: theme.spacing(3),
        paddingBottom: 12,
      },
      heading: {
        marginLeft: 0,
        marginRight: 0,
        marginBottom: 20,
      },
      contentWrap: {
        justifyContent: 'space-between',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100%)',
      },
      contentClass: {
        margin: '0 auto auto',
      },
      tableDefault: {
        height: `calc(100vh - ${rowData?.length ? '270px' : '350px'}) !important`,
        fontFamily: theme.typography.fontFamily,
      },
      paddingX: {
        paddingLeft: theme.spacing(0),
        paddingRight: theme.spacing(0),
        width: 'auto',
      },
      noRows: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 220,
        justifyContent: 'center',
      },
      personBackground: {
        backgroundColor: '#696d7033',
        borderRadius: 50,
      },
      dashboardMetricCard: {
        width: '20%',
      },
    }),
  );
  const classes = useStyles();

  const totalSelectedAmount = selectedRows.reduce((left, right) => {
    return left + right.amount;
  }, 0);

  if (!isRemittanceReceivablesNavEnabled) {
    return <></>;
  }

  const renderBottomAppBar = () => {
    return (
      selectedRows.length > 0 && (
        <BottomAppBar
          primaryActionLabel="Modify Assignment"
          primaryActionDataQa={'Remittance-Receivables-Modify-Assignment'}
          disablePrimary={!selectedRows.every((selectedRow) => selectedRow.lockboxName == selectedRows[0].lockboxName)}
          cancelLabel="Clear All"
          cancelDataQa={'Remittance-Receivables-Clear-All'}
          onPrimaryClick={() => {
            setEditAssignmentDrawerOpen(true);
            setEditAssignmentData([...selectedRows]);
          }}
          onCancel={() => gridApiRef?.current?.deselectAll()}
        />
      )
    );
  };

  const rows = filteredRows || rowData;
  const assignedCount = rows.filter((item) => item.status === RemittanceReceivablesStatus.Assigned).length;
  const unassignedCount = rows.filter((item) => item.status === RemittanceReceivablesStatus.Unassigned).length;
  const closedCount = rows.filter((item) => item.status === RemittanceReceivablesStatus.Closed).length;
  const batchesCount = new Set(rows.map((item) => item.batch)).size;
  const amountSum = rows.reduce((sum, item) => sum + item.amount, 0);

  return (
    <ContentContainer fullWidth>
      <Content>
        <div>
          <PageHeader
            className={classes.heading}
            divider={true}
            menuItems={[
              {
                label: 'Knowledge Base',
                link: KBLinks.home,
              },
            ]}
            title={'Remittance Receivables'}
            contentRight={<div style={{ display: 'flex' }}></div>}
          />
          <Grid container spacing={2}>
            <Grid item xs="auto" className={classes.dashboardMetricCard}>
              <DashboardMetricCard heading="Assigned Items" status="Searching" value={assignedCount.toString()} />
            </Grid>
            <Grid item xs="auto" className={classes.dashboardMetricCard}>
              <DashboardMetricCard
                heading="Unassigned Items"
                status="Quality Review"
                value={unassignedCount.toString()}
              />
            </Grid>
            <Grid item xs="auto" className={classes.dashboardMetricCard}>
              <DashboardMetricCard heading="Closed Items" status="Examining" value={closedCount.toString()} />
            </Grid>
            <Grid item xs="auto" className={classes.dashboardMetricCard}>
              <DashboardMetricCard heading="Batches" status="Searching" value={batchesCount.toString()} />
            </Grid>
            <Grid item xs="auto" className={classes.dashboardMetricCard}>
              <DashboardMetricCard heading="Amount" status="Quality Review" value={formatUSD(amountSum)} />
            </Grid>
            <Grid item xs={12}>
              <div>
                <DataTable
                  defaultColDef={{
                    editable: false,
                    filter: 'agTextColumnFilter',
                    flex: 1,
                    floatingFilter: true,
                    resizable: true,
                    sortable: true,
                    suppressFloatingFilterButton: true,
                    suppressHeaderFilterButton: true,
                    suppressHeaderMenuButton: true,
                    suppressSizeToFit: false,
                    width: 144,
                    minWidth: 144,
                  }}
                  autoGroupColumnDef={{
                    minWidth: 200,
                  }}
                  rowHeight={40}
                  rowGroupPanelShow="always"
                  animateRows={true}
                  noRowsOverlayComponent={'customOverlayNoRows'}
                  groupDefaultExpanded={-1}
                  gridOptions={{
                    suppressRowClickSelection: true,
                    rowSelection: 'multiple',
                    groupSelectsChildren: true,
                  }}
                  onSortChanged={(event: SortChangedEvent) => {
                    event.columns?.forEach((column) => {
                      colDef.forEach((col) => {
                        if (col.colId === column.getColId()) {
                          col.sort = column.getSort();
                        } else {
                          col.sort = undefined;
                        }
                      });
                    });
                    setColDef([...colDef]);
                  }}
                  onGridReady={(event) => {
                    gridApiRef.current = event.api;
                    setDisplayedRowCount(event.api.getDisplayedRowCount());
                  }}
                  onFilterChanged={(event: FilterChangedEvent<RemittanceReceivable>) => {
                    const filteredRows: RemittanceReceivable[] = [];
                    event.api.forEachNodeAfterFilterAndSort((node) => {
                      if (node.data) {
                        filteredRows.push(node.data);
                      }
                    });
                    setFilteredRows(filteredRows);
                    setDisplayedRowCount(event.api.getDisplayedRowCount());
                  }}
                  onSelectionChanged={(event: SelectionChangedEvent<RemittanceReceivable>) => {
                    setSelectedRows(event.api.getSelectedRows());
                  }}
                  columnDefs={colDef}
                  domLayout="autoHeight"
                  rowData={rowData}
                  maintainColumnOrder
                  fixedHeader
                  alwaysShowHorizontalScroll
                />
              </div>
              {totalSelectedAmount > 0 && displayedRowCount > 0 && (
                <Typography variant="body2">
                  Selecting {selectedRows.length} items for {formatUSD(totalSelectedAmount)}
                </Typography>
              )}
              <Typography variant="body2">
                Showing {displayedRowCount} of {rowData.length} items
              </Typography>
            </Grid>
          </Grid>
          {renderBottomAppBar()}
          {modifyAssignmentDrawerOpen && (
            <RemittanceReceivablesModifyAssignmentContainer
              open={modifyAssignmentDrawerOpen}
              setOpen={setEditAssignmentDrawerOpen}
              data={editAssignmentData}
              onSaved={() => {
                setRowData([...rowData]);
              }}
            />
          )}
        </div>
      </Content>
    </ContentContainer>
  );
}
